import {
  type CleanLocationDraftForServer,
  type ScreenerLocation,
  type ScreenerLocationDraft,
  type Wages,
  WageType,
} from "@/types/screener-location";
import { isPositive, type Nullable } from "@/types/utils";
import { numberIsTruthyOrZero } from "@/utils/helpers";

const includesWages = (location: Nullable<Wages>) =>
  !!(
    location &&
    (numberIsTruthyOrZero(location.maxPayHourly) ||
      numberIsTruthyOrZero(location.minPayHourly))
  );

// Simple re-assignment for clarity while maintaining type safety
const valueIsValidWage = isPositive;

const getWageType = (location: Wages) => {
  if (!includesWages(location)) {
    return WageType.DontInclude;
  }

  const { maxPayHourly, minPayHourly } = location;

  if (
    numberIsTruthyOrZero(maxPayHourly) &&
    numberIsTruthyOrZero(minPayHourly)
  ) {
    if (minPayHourly === maxPayHourly) {
      return WageType.SingleWage;
    }

    return WageType.WageRange;
  }

  return WageType.SingleWage;
};

// This function is for dealing with legacy wage behavior. `wageType` is purely
// a frontend concept inferred from the wages on the location. In the DB, either
// or both wage fields can be undefined. Because we treat `minPayHourly` as the
// "true" value for `SingleWage` on the FE we need to handle the case where
// `maxPayHourly` is the only wage defined. It was determined that our best
// approach for avoiding edge cases with `SingleWage` would be to set both
// values to be equal.
// Not intended for use on ScreenerLocationDrafts
export const getDraftWageInfoFromLocation = (
  location: ScreenerLocation,
): Pick<
  ScreenerLocationDraft,
  "maxPayHourly" | "minPayHourly" | "wageType"
> => {
  const { maxPayHourly, minPayHourly } = location;
  const wageType = getWageType(location);
  const hasMinWage = numberIsTruthyOrZero(minPayHourly);
  const hasMaxWage = numberIsTruthyOrZero(maxPayHourly);

  if (wageType === WageType.SingleWage) {
    // If both values exist and WageType is SingleWage, then the wages are
    // already equal. Pass them through as is
    if (hasMinWage && hasMaxWage) {
      return {
        maxPayHourly,
        minPayHourly,
        wageType,
      };
    }

    // When missing max wage, fill the value with minWage
    if (!hasMaxWage) {
      return {
        maxPayHourly: minPayHourly,
        minPayHourly,
        wageType,
      };
    }

    // Same as above, but for min wage
    return {
      maxPayHourly,
      minPayHourly: maxPayHourly,
      wageType,
    };
  }

  // All other wage types can be passed through as is
  return {
    maxPayHourly,
    minPayHourly,
    wageType,
  };
};

export const draftWagesAreValid = (
  location: Nullable<ScreenerLocationDraft>,
): [valid: false, message: string] | [valid: true, message: undefined] => {
  if (!location) {
    return [false, "Location not found"];
  }
  const { maxPayHourly, minPayHourly, wageType } = location;
  const minWageValid = valueIsValidWage(minPayHourly);
  const maxWageValid = valueIsValidWage(maxPayHourly);
  const validReturnValue: [true, undefined] = [true, undefined];

  switch (wageType) {
    case WageType.DontInclude:
      // Values don't matter in this case as wages will be stripped from the
      // draft on save
      return validReturnValue;
    case WageType.SingleWage:
      // Wage type of single wage only references min pay
      return minWageValid
        ? validReturnValue
        : [false, "Please enter a valid wage"];
    case WageType.WageRange:
      if (!minWageValid && !maxWageValid) {
        return [false, "Please enter a valid minimum and maximum wage"];
      }

      if (!minWageValid) {
        return [false, "Please enter a valid minimum wage"];
      }

      if (!maxWageValid) {
        return [false, "Please enter a valid maximum wage"];
      }

      if (minPayHourly > maxPayHourly) {
        return [false, "Maximum wage must be higher than minimum wage"];
      }

      return validReturnValue;
  }
};

// This function is intended only as a formatter and is expected to be called
// only after wages have been validated. The errors should never throw in
// production and serve as a dev safety check and to make TS happy
export const formatValidatedDraftWagesForServer = ({
  maxPayHourly,
  minPayHourly,
  wageType,
}: ScreenerLocationDraft): Pick<
  CleanLocationDraftForServer,
  "maxPayHourly" | "minPayHourly"
> => {
  const minPayIsValid = valueIsValidWage(minPayHourly);
  const maxPayIsValid = valueIsValidWage(maxPayHourly);

  const errorText = `
Invalid wage value found when formatting wages for server.
  
Wages: \`{ minPayHourly: ${minPayHourly}, maxPayHourly: ${maxPayHourly} }\`
  `.trim();

  switch (wageType) {
    case WageType.DontInclude:
      return { maxPayHourly: null, minPayHourly: null };
    case WageType.SingleWage:
      if (!minPayIsValid) {
        throw new Error(errorText);
      }

      // For single wages, we set both min and max to be equal to avoid issues
      // with legacy behavior where `maxPayHourly` can be defined while
      // `minPayHourly` is not. See comment on `getDraftWageInfoFromLocation`
      // above for more information
      return { maxPayHourly: minPayHourly, minPayHourly };
    case WageType.WageRange:
      if (!minPayIsValid || !maxPayIsValid) {
        throw new Error(errorText);
      }

      return { maxPayHourly, minPayHourly };
  }
};
